import { ago, type FleetNode, type LiceRow, type Me } from '../api'
import { useApi } from '../useApi'
import { Empty, ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { Link } from '../router'
import { useT } from '../lang'

/* What a customer sees first: their farms, not our equipment.
 *
 * The staff dashboard lands on the fleet, because the question there is "is my
 * hardware working". A fish farmer's question is "what is happening at my
 * site", and the hydrophone is a detail inside the answer rather than the
 * headline. Same data, different unit of attention.
 *
 * One card per farm, and each card answers three things without being opened:
 * is it listening, is the lice count near the limit, and what does the model
 * think happens over the next four weeks.
 */

interface SiteRow {
  site_id: string
  name: string
  municipality: string | null
  active_mission_id: string | null
}

/** The colour and word for a lice status. `approaching` is Phoniq's own early
 *  warning at 80% of the limit and is worded so it cannot be mistaken for the
 *  regulator's line. */
const STATUS: Record<string, { cls: string; label: string }> = {
  over: { cls: 'bad', label: 'Over grensen' },
  approaching: { cls: 'warn', label: 'Nærmer seg' },
  under: { cls: 'ok', label: 'Under grensen' },
  unknown: { cls: '', label: 'Ikke talt' },
}

export function Home({ me }: { me: Me }) {
  const t = useT()
  const sites = useApi<{ items: SiteRow[] }>('/v1/sites?limit=200')
  // Polled, because "is it listening now" is the one thing on this page that
  // changes while you look at it.
  //
  // Not for a device. The receiver keeps /v1/fleet closed to that credential,
  // and a 401 is how the shell learns a session has ended -- so an admitted
  // tablet landing here was sent back to /koble within a second, by a read it
  // never needed. A device gets the farms without the listening dot.
  const fleet = useApi<{ items: FleetNode[] }>(
    me.via === 'device' ? null : '/v1/fleet', { poll: 20_000 })
  const lice = useApi<{ items: LiceRow[] }>('/v1/lice')

  if (sites.cold) return <Rows n={4} />
  if (sites.error) return <ErrorBox error={sites.error} />

  const rows = sites.data?.items ?? []
  const nodeFor = (siteId: string) =>
    (fleet.data?.items ?? []).find((n) => n.site_id === siteId)
  const liceFor = (siteId: string) =>
    (lice.data?.items ?? []).find((l) => l.site_id === siteId)

  return (
    <>
      <PageHead
        title={t('Your farms')}
        lead={me.orgs?.[0]?.name ?? undefined}
      />

      {rows.length === 0 ? (
        <Empty title={t('Nothing here yet')}>
          {t('No farms have been assigned to you. Phoniq sets that up.')}
        </Empty>
      ) : (
        <div className="farm-grid">
          {rows.map((s) => (
            <FarmCard key={s.site_id} site={s} me={me}
              node={nodeFor(s.site_id)} lice={liceFor(s.site_id)} />
          ))}
        </div>
      )}
    </>
  )
}

function FarmCard({ site, node, lice, me }: {
  site: SiteRow; node?: FleetNode; lice?: LiceRow; me: Me
}) {
  const t = useT()
  const to = `/sites/${encodeURIComponent(site.site_id)}`
  /* The door to the audio a customer has paid for, and it now leads somewhere
   * built for it rather than to a staff tab.
   *
   * Addressed by SITE. It used to be `/missions/{active_mission_id}/listen`,
   * and that id is `ORDER BY started_at DESC LIMIT 1` over a farm's active
   * deployments -- fine while every farm has one hydrophone, and wrong the
   * moment one has two. On the only two-sensor farm in the fleet it resolved
   * to the box with no archive at all, so this link opened the empty one. The
   * player asks the site for every hydrophone in the water and lets a person
   * choose.
   *
   * `active_mission_id` is still the test for whether to show the link -- it
   * means "something is deployed here" and is right for that. */
  const canListen = !!site.active_mission_id && me.can?.listen !== false
  const status = STATUS[lice?.current.status ?? 'unknown'] ?? STATUS['unknown']!

  // "Listening" means data arriving, never the mission state. A mission can say
  // active while a node has been silent for a week, and the card would then be
  // confidently wrong about the one thing somebody checks it for.
  const heard = node?.last_seen_at
  const live = heard && (Date.now() - new Date(heard).getTime()) < 5 * 60_000

  return (
    <div className="farm-card">
      {/* The body is the link, not the card: a Listen link nested inside a
          card-wide link would be an anchor inside an anchor. */}
      <Link to={to} className="farm-card-body">
      <div className="farm-card-head">
        <div>
          <h2>{site.name}</h2>
          {site.municipality && <p className="faint">{site.municipality}</p>}
        </div>
        <span className={live ? 'pulse on' : 'pulse'} aria-hidden="true" />
      </div>

      <dl className="farm-card-facts">
        <div>
          <dt>{t('Hydrophone')}</dt>
          <dd>
            {!node ? t('none deployed')
              : live ? t('listening now')
                : heard ? `${t('last heard')} ${ago(heard)}`
                  : t('not heard from')}
          </dd>
        </div>

        {lice && (
          <div>
            <dt>{t('Lice')}</dt>
            <dd>
              <span className={`chip ${status.cls}`}>{t(status.label)}</span>
              {lice.current.adult_female != null && (
                <span className="faint">
                  {' '}{lice.current.adult_female.toFixed(2)}
                  {lice.current.threshold != null &&
                    ` ${t('of')} ${lice.current.threshold}`}
                </span>
              )}
              {/* WHICH NUMBER THIS IS, on the card as well as on the farm page.
                *
                * The receiver picks between two sources that routinely
                * disagree: the farm's own count, and the register's monthly
                * average published weeks behind. The card printed the winner
                * and said nothing about which had won -- so a farm that counted
                * 0,30 on Wednesday, then withdrew that count, read 0,10 here
                * with no way to tell that the figure had fallen back to the
                * register and their own number was no longer in play. A lice
                * figure whose provenance is invisible is the one thing the lice
                * endpoint is written not to produce. */}
              {lice.current.adult_female != null && (
                <div className="faint" style={{ fontSize: 12 }}>
                  {lice.current.source === 'own_count'
                    ? t('counted at the farm')
                    : t('reported to the register')}
                  {lice.current.as_of ? ` ${lice.current.as_of}` : ''}
                </div>
              )}
            </dd>
          </div>
        )}
      </dl>
      </Link>

      {canListen && (
        <Link className="btn ghost sm farm-card-lytt" to={`${to}/lytt`}>
          {t('Listen')}
        </Link>
      )}
    </div>
  )
}
